import { DEFAULT_OSP_APP_ID, getEnvIndexBaseUrl, HEADERS } from "@api/osp";
import { cache } from "react";
import { userAgent } from "next/server";
import { I18nUtil } from "@utils/i18n";
import { MemberClient } from "../features/api/memberApi";
import { GetRankingThemeRequest, OpenDataClient } from "@features/api/openData";

import { ChatClient } from "@features/api/ChatApi";
import { config } from "@config/config";

type UserAgent = ReturnType<typeof userAgent>;

/** SSR：与 SDK AuthModule 构造参数一致，均为 env / appId 静态配置 */
const getSsrOspIndexBaseUrl = cache(() =>
  getEnvIndexBaseUrl(config.osp.environment),
);

const getSsrOspAppId = cache(() => config.osp.appId);

const getSsrOspAppIdWithDefault = cache(
  () => config.osp.appId || DEFAULT_OSP_APP_ID,
);

// 生成设备指纹的函数 - 使用cache确保同一请求周期内使用相同指纹
const generateDeviceFingerprint = cache(async () => {
  const timestamp = Date.now().toString();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}`;
});

export const getOpenDataApi = cache(() => {
  const openDataApiClient = new OpenDataClient({
    baseUrl: `${getSsrOspIndexBaseUrl()}/opendata`,
    baseApiParams: {
      secure: true,
      headers: {
        [HEADERS.APP_ID]: getSsrOspAppIdWithDefault(),
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
      },
    },
  });

  return openDataApiClient;
});

export const getMemberApi = cache(async () => {
  const deviceFingerprint = await generateDeviceFingerprint();
  const memberApiClient = new MemberClient({
    baseUrl: getSsrOspIndexBaseUrl(),
    baseApiParams: {
      secure: true,
      headers: {
        "OS-Device-Id": deviceFingerprint,
        [HEADERS.APP_ID]: getSsrOspAppId(),
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
      },
    },
  });
  return memberApiClient;
});

export const getChatApi = cache(async () => {
  const deviceFingerprint = await generateDeviceFingerprint();
  const chatApiClient = new ChatClient({
    baseUrl: `${config.api.baseUrl.chat}`,
    baseApiParams: {
      secure: true,
      headers: {
        guestId: deviceFingerprint,
      },
    },
  });

  return chatApiClient;
});

// 无法准确判断设备的时候，就返回移动端，移动端优先
export const getDeviceType = cache(
  (
    device: UserAgent["device"],
    os: UserAgent["os"],
  ): "tablet" | "mobile" | "desktop" => {
    if (!!device.type && device.type === "tablet") {
      return "tablet";
    }
    if (!!os.name) {
      const osName = os.name.toLowerCase().replace(/\s/g, "");
      if (osName === "windows") {
        return "desktop";
      }
      if (osName === "macos") {
        return "desktop";
      }
    }
    return "mobile";
  },
);

export const getHomRankingThemes = cache(
  async ({
    type,
    limit,
    symbolLimit,
    withMarket,
    withSymbol,
    market,
    locale,
  }: Pick<GetRankingThemeRequest, "type" | "limit" | "market"> & {
    symbolLimit: number;
    withMarket: boolean;
    withSymbol: boolean;
    locale: string;
  }) => {
    const openDataApi = getOpenDataApi();
    const language = I18nUtil.getBackgroundLanguageKey(locale);
    const response = await openDataApi?.themes.getRankingThemes({
      // TODO ZJL, 这里需要添加market参数
      offset: 0,
      limit: limit,
      type: type,
      market,
      "sort.field": "PRICE_CHANGE_24H",
      "sort.order": "DESC",
      "enrich.with_market.enable": withMarket,
      "enrich.with_symbol.enable": withSymbol,
      "enrich.with_symbol.limit": symbolLimit,
      "enrich.enable": true,
      language: language,
    });
    return response?.data?.data?.rows;
  },
);
